<template>
  <div class="notes-page">
    <div class="page-container">
      <!-- 侧边栏：分类 -->
      <aside class="sidebar">
        <div class="sidebar-header">
          <h3>我的笔记</h3>
          <button @click="showAddCategoryModal = true" class="btn-icon add-btn">+</button>
        </div>
        <ul class="category-list">
          <li 
            v-for="cat in categories" 
            :key="cat.id" 
            :class="{ active: currentCategory === cat.name }"
            @click="selectCategory(cat)"
            class="category-item"
          >
            <span class="category-name">{{ cat.name }}</span>
            <div class="category-actions" @click.stop>
              <button @click="editCategory(cat)" class="btn-icon">✏️</button>
              <button @click="deleteCategory(cat)" class="btn-icon delete">🗑️</button>
            </div>
          </li>
          <li v-if="categories.length === 0" class="empty-cat">暂无分类</li>
        </ul>
      </aside>

      <!-- 主内容区：笔记列表和详情 -->
      <main class="main-content">
        <div v-if="!currentCategory" class="welcome-screen">
          <div class="welcome-box">
            <h2>欢迎使用笔记</h2>
            <p>请选择或创建一个分类开始记录</p>
          </div>
        </div>
        
        <div v-else class="notes-area">
           <div class="notes-header">
             <h2>{{ currentCategory }} <span class="count">({{ totalNotes }})</span></h2>
             <button @click="showAddNoteModal = true" class="btn-primary">
               + 新建笔记
             </button>
           </div>
           
           <div v-if="loading" class="loading">加载中...</div>
           <div v-else-if="notes.length === 0" class="empty-notes">
             <p>该分类下暂无笔记</p>
           </div>
           
           <template v-else>
             <div class="notes-grid">
               <div v-for="note in notes" :key="note.id" class="note-card" @click="viewNote(note)">
                 <div v-if="note.isPinned" class="pin-mark">📌</div>
                 <h3 class="note-title">{{ note.title || '无标题' }}</h3>
                 <p class="note-preview">{{ truncateText(note.content, 50) }}</p>
                 <div class="note-footer">
                   <button v-if="note.isPinned" @click.stop="updatePinStatus(note, 0)" class="btn-text">取消置顶</button>
                   <button v-else @click.stop="updatePinStatus(note, 1)" class="btn-text">置顶</button>
                   <button @click.stop="deleteNote(note)" class="btn-text delete">删除</button>
                 </div>
               </div>
             </div>
             <div class="pagination-wrap">
               <button class="pagination-btn" :disabled="notePageNo <= 1" @click="goNotePage(notePageNo - 1)">上一页</button>
               <span class="pagination-info">第 {{ notePageNo }} / {{ totalNotePages }} 页，共 {{ totalNotes }} 条</span>
               <button class="pagination-btn" :disabled="notePageNo >= totalNotePages" @click="goNotePage(notePageNo + 1)">下一页</button>
             </div>
           </template>
        </div>
      </main>
    </div>

    <!-- 笔记编辑/查看弹窗，编辑时左右分屏 -->
    <div v-if="showNoteModal" class="modal full-screen-modal">
      <div class="modal-content">
        <div class="modal-header">
          <div class="header-toolbar">
            <div class="header-left">
              <button @click="closeNoteModal" class="btn-back">← 返回</button>
            </div>
            <div class="header-right">
              <template v-if="!isViewMode">
                <button @click="toggleEditorMode" class="btn-switch">
                  {{ useMarkdown ? '切换文本' : '切换MD' }}
                </button>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="currentNote.isPinned" :true-value="1" :false-value="0"> 置顶
                </label>
                <button @click="saveNote" class="btn-save" :disabled="saving">
                  {{ saving ? '保存中...' : '保存' }}
                </button>
              </template>
              <template v-else>
                <button @click="toggleReadingMode" class="btn-reading" :class="{ active: isReadingMode }">
                  {{ isReadingMode ? '退出阅读' : '英语阅读' }}
                </button>
                <button @click="switchToEdit" class="btn-edit">编辑</button>
              </template>
            </div>
          </div>
          <div class="header-title-row" v-if="!isViewMode">
            <input
              v-model="currentNote.title"
              type="text"
              class="title-input"
              placeholder="请输入标题"
              :readonly="isViewMode"
            >
          </div>
        </div>
        <div class="modal-body note-editor-body">
          <div v-if="isReadingMode" class="reading-mode-container">
            <div class="reading-content">
              <template v-for="(segment, index) in parsedContent">
                <span
                  v-if="/^[a-zA-Z]+(?:['\-_][a-zA-Z]+)*$/.test(segment)"
                  :key="'w-' + index"
                  class="clickable-word"
                  @click="handleWordClick(segment)"
                >{{ segment }}</span>
                <span v-else :key="'t-' + index" class="text-segment" style="white-space: pre-wrap;">{{ segment }}</span>
              </template>
            </div>
          </div>
          <mavon-editor
            v-else-if="isViewMode"
            key="note-view"
            v-model="currentNote.content"
            :subfield="false"
            defaultOpen="preview"
            :toolbarsFlag="false"
            :editable="false"
            :boxShadow="false"
            style="height: 100%; min-height: 500px; z-index: 1;"
          />
          <template v-else>
            <mavon-editor
              v-if="useMarkdown"
              key="note-edit-md"
              v-model="currentNote.content"
              style="height: 100%; min-height: 500px; z-index: 1;"
              placeholder="在此输入笔记内容(支持Markdown)..."
            />
            <textarea
              v-else
              key="note-edit-text"
              v-model="currentNote.content"
              class="content-editor"
              placeholder="在此输入笔记内容..."
            ></textarea>
          </template>
        </div>
      </div>
    </div>
    
    <!-- 单词详情弹窗 -->
    <WordDetailModal
      :visible.sync="showWordDetail"
      :word-name="readingWordName"
      :show-add-button="true"
    />

    <!-- 分类管理弹窗 -->
    <div v-if="showAddCategoryModal || showEditCategoryModal" class="modal">
      <div class="modal-content small-modal">
        <div class="modal-header">
          <h3>{{ showEditCategoryModal ? '编辑分类' : '新建分类' }}</h3>
          <button @click="closeCategoryModal" class="btn-close">×</button>
        </div>
        <div class="modal-body">
          <input 
            v-model="categoryFormName" 
            type="text" 
            placeholder="分类名称"
            class="input-field"
          >
        </div>
        <div class="modal-footer">
          <button @click="closeCategoryModal" class="btn-cancel">取消</button>
          <button @click="handleCategorySubmit" class="btn-confirm">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getNoteCategories,
  addNoteCategory,
  updateCategory,
  deleteCategory,
  getNotesByCategory,
  addNote,
  updateNote,
  deleteNote
} from '@/api/note'
import WordDetailModal from './WordDetailModal.vue'

export default {
  name: 'NotesPage',
  components: {
    WordDetailModal
  },
  data() {
    return {
      userName: localStorage.getItem('userName'),
      categories: [],
      notes: [],
      currentCategory: '',
      loading: false,
      notePageNo: 1,
      notePageSize: 20,
      totalNotes: 0,
      
      // 分类模态框
      showAddCategoryModal: false,
      showEditCategoryModal: false,
      categoryFormName: '',
      oldCategoryName: '',
      
      // 笔记模态框
      showAddNoteModal: false,
      showNoteModal: false,
      currentNote: {
        id: null,
        title: '',
        content: '',
        isPinned: 0,
        categoryName: ''
      },
      isEditingNote: false,
      isViewMode: false,
      useMarkdown: true,
      
      // 英语阅读模式
      isReadingMode: false,
      showWordDetail: false,
      readingWordName: '',
      saving: false
    }
  },
  computed: {
    parsedContent() {
      if (!this.currentNote.content) return []
      return this.currentNote.content.split(/([a-zA-Z]+(?:['\-_][a-zA-Z]+)*)/g)
    },
    totalNotePages() {
      if (this.totalNotes <= 0 || this.notePageSize <= 0) return 1
      return Math.ceil(this.totalNotes / this.notePageSize) || 1
    }
  },
  watch: {
    showAddNoteModal(val) {
      if (val) {
        this.currentNote = {
          title: '',
          content: '',
          isPinned: 0,
          categoryName: this.currentCategory
        }
        this.isEditingNote = false
        this.isViewMode = false
        this.useMarkdown = true
        this.isReadingMode = false
        this.showNoteModal = true
        this.showAddNoteModal = false // 立即切换到 full modal
      }
    }
  },
  mounted() {
    this.fetchCategories()
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await getNoteCategories(this.userName)
        if (res.data.success) {
          this.categories = res.data.data || []
          // 如果没有选中的分类且有分类数据，默认选第一个
          if (!this.currentCategory && this.categories.length > 0) {
            this.selectCategory(this.categories[0])
          }
        }
      } catch (error) {
        console.error('获取分类失败', error)
      }
    },
    async selectCategory(cat) {
      this.currentCategory = cat.name
      this.notePageNo = 1
      this.fetchNotes()
    },
    goNotePage(page) {
      if (page < 1 || page > this.totalNotePages) return
      this.notePageNo = page
      this.fetchNotes()
    },
    async fetchNotes() {
      this.loading = true
      try {
        const res = await getNotesByCategory(this.userName, this.currentCategory, this.notePageNo, this.notePageSize)
        if (res.data.success && res.data.data) {
          const raw = res.data.data
          const list = raw.items || []
          this.notes = list.map(n => ({ ...n, isPinned: Number(n.isPinned) || 0 }))
          this.totalNotes = Number(raw.total) || 0
        } else {
          this.notes = []
          this.totalNotes = 0
        }
      } catch (error) {
        console.error('获取笔记失败', error)
        this.notes = []
        this.totalNotes = 0
      } finally {
        this.loading = false
      }
    },
    
    // 分类管理
    editCategory(cat) {
      this.oldCategoryName = cat.name
      this.categoryFormName = cat.name
      this.showEditCategoryModal = true
    },
    async deleteCategory(cat) {
      if (!confirm(`确定删除分类 "${cat.name}" 及其所有笔记吗？`)) return
      try {
        const res = await deleteCategory(this.userName, cat.name)
        if (res.data.success) {
          if (this.currentCategory === cat.name) {
            this.currentCategory = ''
            this.notes = []
          }
          this.fetchCategories()
        }
      } catch (error) {
        console.error('删除失败', error)
      }
    },
    async handleCategorySubmit() {
      if (!this.categoryFormName.trim()) return
      
      try {
        if (this.showEditCategoryModal) {
          const res = await updateCategory(this.userName, this.oldCategoryName, { categoryName: this.categoryFormName })
          if (res.data.success) {
            // 如果修改的是当前分类，更新当前分类名
            if (this.currentCategory === this.oldCategoryName) {
              this.currentCategory = this.categoryFormName
            }
            this.fetchCategories()
            this.closeCategoryModal()
          }
        } else {
          const res = await addNoteCategory(this.userName, { categoryName: this.categoryFormName })
          if (res.data.success) {
            this.fetchCategories()
            this.closeCategoryModal()
          }
        }
      } catch (error) {
        console.error('操作失败', error)
        this.$message.error('操作失败')
      }
    },
    closeCategoryModal() {
      this.showAddCategoryModal = false
      this.showEditCategoryModal = false
      this.categoryFormName = ''
      this.oldCategoryName = ''
    },
    
    // 笔记管理
    viewNote(note) {
      this.currentNote = { ...note, categoryName: this.currentCategory }
      this.isEditingNote = true
      this.isViewMode = true
      this.isReadingMode = false
      this.showNoteModal = true
    },
    switchToEdit() {
      this.isViewMode = false
      this.useMarkdown = true
      this.isReadingMode = false
    },
    toggleEditorMode() {
      this.useMarkdown = !this.useMarkdown
    },
    toggleReadingMode() {
      if (this.isReadingMode) {
        this.isReadingMode = false
        return
      }
      
      // 检查内容是否为全英文（宽松检查：英文字符占比超过50%）
      const content = this.currentNote.content || ''
      if (!content.trim()) {
        this.$message.warning('内容为空，无法开启阅读模式')
        return
      }
      
      const englishCharCount = (content.match(/[a-zA-Z]/g) || []).length
      const totalCount = content.length
      
      if (totalCount > 0 && (englishCharCount / totalCount) < 0.5) {
        this.$message.warning('当前内容似乎不是英文文章，无法开启英语阅读模式')
        return
      }
      
      this.isReadingMode = true
    },
    handleWordClick(word) {
      // 过滤掉标点符号和空白字符
      const cleanWord = word.replace(/[^a-zA-Z'-]/g, '')
      if (cleanWord && cleanWord.length > 1) {
        this.readingWordName = cleanWord
        this.showWordDetail = true
      }
    },
    async saveNote() {
      if (this.saving) return
      if (!this.currentNote.title && !this.currentNote.content) {
        this.$message.warning('标题或内容不能为空')
        return
      }
      
      this.saving = true
      try {
        if (this.isEditingNote) {
          // 更新笔记
          // 注意：后端更新接口需要 Note 对象结构
          const updateData = {
            title: this.currentNote.title,
            content: this.currentNote.content,
            isPinned: Number(this.currentNote.isPinned) || 0,
            categoryName: this.currentCategory
          }
          const res = await updateNote(this.userName, this.currentNote.id, updateData)
          if (res.data.success) {
            this.fetchNotes()
            this.closeNoteModal()
          } else {
            this.$message.error('更新失败: ' + res.data.errMsg)
          }
        } else {
          // 添加笔记
          const addData = {
            title: this.currentNote.title,
            content: this.currentNote.content,
            isPinned: Number(this.currentNote.isPinned) || 0
          }
          const res = await addNote(this.userName, this.currentCategory, addData)
          if (res.data.success) {
            this.fetchNotes()
            this.closeNoteModal()
          } else {
            this.$message.error('添加失败: ' + res.data.errMsg)
          }
        }
      } catch (error) {
        console.error('保存失败', error)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    async updatePinStatus(note, status) {
      try {
        const updateData = {
          title: note.title,
          content: note.content,
          isPinned: status,
          categoryName: this.currentCategory
        }
        const res = await updateNote(this.userName, note.id, updateData)
        if (res.data.success) {
          this.fetchNotes()
        }
      } catch (error) {
        console.error('更新置顶状态失败', error)
      }
    },
    async deleteNote(note) {
      if (!confirm('确定删除这条笔记吗？')) return
      try {
        const res = await deleteNote(note.id)
        if (res.data.success) {
          if (this.notes.length === 1 && this.notePageNo > 1) this.notePageNo--
          this.fetchNotes()
        }
      } catch (error) {
        console.error('删除失败', error)
      }
    },
    closeNoteModal() {
      this.showNoteModal = false
      this.isViewMode = false
      this.currentNote = {
        id: null,
        title: '',
        content: '',
        isPinned: 0,
        categoryName: ''
      }
    },
    truncateText(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }
  }
}
</script>

<style scoped>
.notes-page {
  min-height: calc(100vh - 80px);
  padding: 20px;
  background: var(--bg-color);
  box-sizing: border-box;
}

.page-container {
  display: flex;
  height: calc(100vh - 120px);
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
}

.sidebar {
  width: 260px;
  min-width: 260px;
  background: var(--surface);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 18px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border);
  background: var(--surface-card);
}

.sidebar-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--text-color);
  font-weight: 700;
  letter-spacing: -0.02em;
}

.add-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  border: none;
  background: var(--accent);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.25rem;
  line-height: 1;
  transition: background 0.2s, transform 0.2s;
}

.add-btn:hover {
  background: var(--accent-hover);
  transform: scale(1.05);
}

.category-list {
  flex: 1;
  overflow-y: auto;
  list-style: none;
  padding: 12px;
  margin: 0;
}

.category-item {
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: var(--radius-md);
  margin-bottom: 4px;
  transition: all 0.2s ease;
  color: var(--text-color);
  font-weight: 500;
}

.category-item:hover {
  background: var(--card-bg);
  transform: translateX(4px);
  box-shadow: var(--shadow-sm);
  color: var(--accent);
}

.category-item.active {
  background: var(--accent);
  color: #fff;
}

.category-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-actions {
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  gap: 8px;
}

.category-item:hover .category-actions {
  opacity: 1;
}

.btn-icon {
  background: white;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  box-shadow: var(--shadow-sm);
}

.category-item.active .btn-icon {
  color: var(--primary-color);
}

.btn-icon:hover {
  transform: scale(1.1);
}

.btn-icon.delete:hover {
  background: var(--danger-color);
  color: #fff;
}

/* 主内容区样式 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.4);
}

.welcome-screen {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #999;
}

.welcome-box {
  text-align: center;
  background: var(--card-bg);
  padding: 40px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 2px dashed var(--border-color);
}

.welcome-box h2 {
  color: var(--primary-color);
  margin-bottom: 10px;
}

.notes-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 30px;
  overflow-y: auto;
}

.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.notes-header h2 {
  margin: 0;
  font-size: 1.8rem;
  color: var(--text-color);
  font-weight: 800;
  position: relative;
  display: inline-block;
}

.notes-header h2::after {
  content: '';
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 8px;
  background: var(--secondary-color);
  opacity: 0.3;
  border-radius: 4px;
  z-index: 0;
}

.count {
  font-size: 1rem;
  color: #999;
  font-weight: normal;
  margin-left: 8px;
}

.notes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
  padding-bottom: 16px;
}

.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 16px 0 24px;
  border-top: 1px solid var(--border);
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-card);
  color: var(--ink);
  cursor: pointer;
  font-size: 0.9rem;
}

.pagination-btn:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--accent);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.9rem;
  color: var(--ink-muted);
}

.note-card {
  background: var(--card-bg);
  padding: 20px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.note-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: var(--primary-color);
  opacity: 0.6;
}

.note-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
  border-color: rgba(232, 93, 117, 0.25);
}

.note-card:nth-child(4n+1)::before { background: var(--primary-color); }
.note-card:nth-child(4n+2)::before { background: var(--secondary-color); }
.note-card:nth-child(4n+3)::before { background: var(--accent-color); }
.note-card:nth-child(4n+4)::before { background: #a78bfa; }

.pin-mark {
  position: absolute;
  top: 15px;
  right: 15px;
  font-size: 1.2rem;
  transform: rotate(45deg);
  filter: drop-shadow(2px 2px 0px rgba(0,0,0,0.1));
}

.note-title {
  margin: 10px 0 10px 0;
  font-size: 1.2rem;
  color: var(--text-color);
  padding-right: 20px;
  font-weight: 700;
}

.note-preview {
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
  flex: 1;
  overflow: hidden;
  margin: 0;
  opacity: 0.8;
}

.note-footer {
  margin-top: 15px;
  text-align: right;
  opacity: 0;
  transition: all 0.3s ease;
  transform: translateY(10px);
}

.note-card:hover .note-footer {
  opacity: 1;
  transform: translateY(0);
}

.btn-text {
  background: none;
  border: none;
  font-size: 0.9rem;
  cursor: pointer;
  color: #999;
  transition: color 0.2s;
  padding: 5px 10px;
  border-radius: var(--border-radius-md);
}

.btn-text:hover {
  background: rgba(0,0,0,0.05);
}

.btn-text.delete:hover {
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
}

/* 模态框样式 */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  z-index: 1100;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.small-modal {
  width: 90%;
  max-width: 400px;
  background: white;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  animation: slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.full-screen-modal .modal-content {
  width: 90%;
  height: 90%;
  max-width: 1000px;
  background: white;
  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: zoomIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes slideUp {
  from { transform: translateY(50px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes zoomIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 2px solid rgba(255, 183, 178, 0.2);
  display: flex;
  flex-direction: column;
  gap: 15px;
  background: white;
  box-shadow: var(--shadow-sm);
}

.modal-header h3 {
  margin: 0;
  color: var(--text-color);
  font-size: 1.3rem;
}

.header-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-title-row {
  width: 100%;
}

.header-left, .header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.modal-body {
  padding: 30px;
  flex: 1;
  overflow-y: auto;
}

.note-editor-body {
  flex: 1;
  background: #fff;
  padding: 0;
  overflow: hidden;
}

.note-editor-body.view-mode {
  padding: 40px 60px;
  background-image: 
    linear-gradient(#f1f1f1 1px, transparent 1px),
    linear-gradient(90deg, #f1f1f1 1px, transparent 1px);
  background-size: 20px 20px;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .note-editor-body.view-mode {
    padding: 20px;
  }
}

.note-view-content {
  width: 100%;
  min-height: 100%;
}

.modal-footer {
  padding: 20px 30px;
  border-top: 1px solid rgba(0,0,0,0.05);
  text-align: right;
  background: rgba(255, 255, 255, 0.9);
}

.input-field {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #eee;
  border-radius: var(--border-radius-md);
  box-sizing: border-box;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.input-field:focus {
  border-color: var(--primary-color);
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 183, 178, 0.2);
}

.title-input {
  font-size: 1.8rem;
  border: none;
  border-bottom: 2px solid transparent;
  outline: none;
  background: transparent;
  width: 100%;
  font-weight: 800;
  color: var(--text-color);
  padding: 5px 0;
  transition: all 0.3s ease;
}

.title-input:focus {
  border-bottom-color: var(--primary-color);
}

.content-editor {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  resize: none;
  font-size: 1.2rem;
  line-height: 1.8;
  color: #555;
  background: transparent;
  font-family: inherit;
}

.btn-primary, .btn-confirm, .btn-save, .btn-edit, .btn-switch {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: var(--border-radius-round);
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.btn-edit {
  background: var(--secondary-color);
}

.btn-switch {
  background: #6c757d; /* 灰色，表示切换功能 */
  margin-right: 10px;
}

.btn-switch:hover {
  background: #5a6268;
}

.btn-primary:hover, .btn-confirm:hover, .btn-save:hover, .btn-edit:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  filter: brightness(1.1);
}

.btn-cancel {
  background: #f1f3f5;
  color: #666;
  padding: 10px 25px;
  border-radius: var(--border-radius-round);
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background: #e9ecef;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #ccc;
  transition: color 0.2s;
  line-height: 1;
}

.btn-close:hover {
  color: #ff6b6b;
}

.btn-back {
  background: white;
  border: 1px solid #eee;
  color: #666;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 8px 15px;
  border-radius: var(--border-radius-round);
  transition: all 0.2s;
  box-shadow: var(--shadow-sm);
}

.btn-back:hover {
  background: #f8f9fa;
  transform: translateX(-3px);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-right: 15px;
  font-size: 1rem;
  color: #666;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .notes-page {
    padding: 10px;
  }

  .page-container {
    flex-direction: column;
    height: calc(100vh - 80px);
  }
  
  .sidebar {
    width: 100%;
    max-height: 180px; /* 限制侧边栏高度 */
    border-right: none;
    border-bottom: 1px solid rgba(0,0,0,0.05);
    flex-shrink: 0;
  }
  
  .category-list {
    display: flex; /* 横向滚动 */
    overflow-x: auto;
    overflow-y: hidden;
    padding: 10px;
    white-space: nowrap;
    gap: 10px;
  }

  .category-item {
    margin-bottom: 0;
    min-width: 120px;
    flex-shrink: 0;
    background: rgba(255,255,255,0.8);
  }

  .main-content {
    border-top: 1px solid #eee;
  }

  .notes-area {
    padding: 15px;
  }

  .notes-header h2 {
    font-size: 1.4rem;
  }

  .notes-grid {
    grid-template-columns: repeat(auto-fill, minmax(100%, 1fr)); /* 单列 */
    gap: 15px;
  }

  .note-card {
    height: 160px;
  }

  .full-screen-modal .modal-content {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .note-detail-body {
    padding: 16px 20px 32px;
  }

  .note-edit-pane,
  .note-view-pane {
    max-width: 100%;
  }

  .title-input {
    font-size: 1.2rem;
  }

  .header-left {
    gap: 10px;
  }
}

@media (max-width: 768px) {
  .note-fullscreen {
    flex-direction: column;
  }

  .note-detail-sidebar {
    width: 100%;
    min-width: 0;
    max-height: 160px;
    border-right: none;
    border-bottom: 1px solid var(--border);
  }

  .note-detail-sidebar .category-list {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 8px;
    gap: 8px;
    white-space: nowrap;
  }

  .note-detail-sidebar .category-item {
    margin-bottom: 0;
    min-width: 100px;
    flex-shrink: 0;
  }
}

/* 简单的Markdown样式 */
.markdown-body h1, .markdown-body h2, .markdown-body h3 {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--text-color);
}

.markdown-body p {
  margin-bottom: 16px;
  color: var(--text-color);
}

.markdown-body a {
  color: var(--primary-color);
  text-decoration: none;
}

.markdown-body a:hover {
  text-decoration: underline;
}

.markdown-body ul, .markdown-body ol {
  padding-left: 2em;
  margin-bottom: 16px;
  color: var(--text-color);
}

.markdown-body blockquote {
  padding: 0 1em;
  color: #8b949e;
  border-left: 0.25em solid #dfe2e5;
  margin-bottom: 16px;
}

.markdown-body pre {
  padding: 16px;
  overflow: auto;
  font-size: 85%;
  line-height: 1.45;
  background-color: rgba(240, 240, 240, 0.5);
  border-radius: 6px;
  margin-bottom: 16px;
}

.markdown-body code {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(27,31,35,0.05);
  border-radius: 3px;
  font-family: SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace;
}

/* 英语阅读模式样式 */
.btn-reading {
  background-color: #17a2b8;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn-reading:hover {
  background-color: #138496;
  transform: translateY(-1px);
}

.btn-reading.active {
  background-color: #6c757d;
}

.reading-mode-container {
  height: 100%;
  overflow-y: auto;
  padding: 40px;
  background-color: #fafafa;
}

.reading-content {
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 20px;
  line-height: 1.8;
  color: #2c3e50;
  max-width: 800px;
  margin: 0 auto;
  text-align: justify;
}

.clickable-word {
  cursor: pointer;
  border-bottom: 1px solid transparent;
  transition: all 0.2s;
  padding: 0 1px;
}

.clickable-word:hover {
  color: #409eff;
  background-color: rgba(64, 158, 255, 0.1);
  border-bottom-color: #409eff;
  border-radius: 2px;
}
</style>
